import { Button, Chip, Divider, Link } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { PATHS } from '../../../router/paths';
import { useCookieConsent } from '../../../components/feature/CookieConsent';

const socialLinks = [
  { icon: 'ri-instagram-line', url: 'https://www.instagram.com/kamilfrajtko', label: 'Instagram' },
  { icon: 'ri-linkedin-line', url: 'https://www.linkedin.com/in/kamilfrajtko/', label: 'LinkedIn' },
];

export default function Footer() {
  const { t } = useTranslation();
  const { openSettings } = useCookieConsent();
  const footerLinks = [
    {
      title: t('footer.navigation'),
      links: [
        { label: t('footer.links.home'), url: PATHS.home },
        { label: t('footer.links.services'), url: PATHS.sluzby },
        { label: t('footer.links.pricing'), url: PATHS.cennik },
        { label: t('footer.links.about'), url: PATHS.omne },
        { label: t('footer.links.contact'), url: PATHS.kontakt },
      ],
    },
    {
      title: t('footer.services'),
      links: [
        { label: t('footer.links.customWebs'), url: PATHS.sluzby },
        { label: t('footer.links.eshops'), url: PATHS.sluzby },
        { label: t('footer.links.automations'), url: PATHS.sluzby },
        { label: t('footer.links.customSolutions'), url: PATHS.sluzby },
      ],
    },
    {
      title: t('footer.contact'),
      links: [
        { label: t('footer.links.contact'), url: PATHS.kontakt },
        { label: t('footer.links.noObligationInquiry'), url: `${PATHS.kontakt}#contact-form` },
        { label: t('footer.links.cookiesSettings'), url: '#' },
      ],
    },
  ];

  const legalLinks = [
    { label: t('footer.links.privacyPolicy'), url: PATHS.ochranaOsobnychUdajov },
    { label: t('footer.links.cookiesPolicy'), url: PATHS.cookies },
    { label: t('footer.links.legalInfo'), url: PATHS.pravneInformacie },
    { label: t('footer.links.termsOfUse'), url: PATHS.podmienkyPouzivania },
  ];

  return (
    <footer className="bg-gradient-to-b from-brand-ocean to-[#01030a] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Chip
              variant="flat"
              className="mb-4 border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-brand-green"
              startContent={
                <span className="mr-2 inline-flex items-center">
                  <i className="ri-briefcase-4-line text-base" />
                </span>
              }
            >
              {t('footer.badge')}
            </Chip>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-brand-green rounded-lg">
                <i className="ri-bar-chart-box-line text-xl text-white"></i>
              </div>
              <span className="text-2xl font-caprasimo">Bc. Kamil Frajtko</span>
            </div>
            <p className="text-brand-grey-light mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <Button
                  key={index}
                  as="a"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  isIconOnly
                  radius="md"
                  variant="flat"
                  className="h-10 w-10 bg-white/10 text-white transition-all hover:bg-brand-green"
                  aria-label={social.label}
                >
                  <i className={`${social.icon} text-lg`}></i>
                </Button>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          {footerLinks.map((column, index) => (
            <div key={index}>
              <h3 className="text-lg font-semibold mb-6">{column.title}</h3>
              <ul className="space-y-4">
                {column.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {link.url === '#' ? (
                      <button
                        type="button"
                        onClick={openSettings}
                        className="text-brand-grey-light transition-colors hover:text-brand-green"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link
                        href={link.url}
                        className="cursor-pointer text-brand-grey-light transition-colors hover:text-brand-green"
                        color="foreground"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <Divider className="bg-white/10" />
        <div className="flex flex-wrap gap-3 py-6">
          {legalLinks.map((link) => (
            <Link
              key={link.url}
              href={link.url}
              className="cursor-pointer rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-brand-grey-light transition-colors hover:text-brand-green"
              color="foreground"
            >
              {link.label}
            </Link>
          ))}
        </div>
        <Divider className="bg-white/10" />
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-brand-grey-light text-sm">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4 text-sm">
            <button
              type="button"
              onClick={openSettings}
              className="text-brand-grey-light transition-colors hover:text-brand-green"
            >
              {t('footer.links.cookiesSettings')}
            </button>
            <Link
              href="https://kamilfrajtko.sk"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-brand-grey-light transition-colors hover:text-brand-green"
              color="foreground"
            >
              {t('footer.site')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
